import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Users, 
  Calendar, 
  MapPin, 
  Clock, 
  TrendingUp, 
  CheckCircle, 
  AlertCircle,
  Activity,
  BarChart3,
  Star,
  ArrowRight,
  Trophy,
  Award,
  Route,
  Plus,
  ArrowLeft
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { useAuth } from "../contexts/AuthContext";
import { useFetch, useApi } from "../hooks/useApi";
import { apiService } from "../services/apiService";
import { transformMeetings, transformClubs, filterAndSortMeetings } from "../utils/dataTransformers";
import Navigation from "./Navigation";
import EventCreationForm from "./EventCreationForm";
import MeetingCard from "./ui/MeetingCard";
import ClubCard from "./ui/ClubCard";
import RunCard from "./ui/RunCard";
import { toast } from "sonner";
import { parseMemberResponse } from "../utils/memberUtils";

// Constants
const FEATURED_CLUBS_LIMIT = 3;
const MAX_PARTICIPANT_AVATARS = 3;

const RunClubDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [featuredClubs, setFeaturedClubs] = useState([]);
  const [showEventForm, setShowEventForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editingRun, setEditingRun] = useState(null);
  const [isShowingNeighboringCities, setIsShowingNeighboringCities] = useState(false);
  const [showConnections, setShowConnections] = useState(false);
  const [connections, setConnections] = useState([]);
  const [loadingConnections, setLoadingConnections] = useState(false);
  const [showConnectionCard, setShowConnectionCard] = useState(false);
  const [selectedConnection, setSelectedConnection] = useState(null);
  const [loadingConnectionCard, setLoadingConnectionCard] = useState(false);

  // Use new hooks for data fetching

  const { data: rawMeetings, loading: meetingsLoading, refetch: refetchMeetings } = useFetch(
    () => {
      return apiService.meetings.getAll({ club_id: user?.club_id });
    },
    [user?.club_id],
    { enabled: !!user?.club_id }
  );


  // Fetch current user's club data
  const { data: clubData, loading: clubLoading, error: clubError, refetch: refetchClub } = useFetch(
    () => apiService.clubs.getById(user?.club_id),
    [user?.club_id],
    { enabled: !!user?.club_id }
  );

  // Transform data using our utilities
  const meetings = useMemo(() => {
    if (!rawMeetings) return [];
    return transformMeetings(rawMeetings);
  }, [rawMeetings]);


  // Filter upcoming events using our utility
  const upcomingEvents = useMemo(() => {
    const filtered = filterAndSortMeetings(meetings, {
      status: 'upcoming',
      sortBy: 'scheduled_date',
      sortOrder: 'asc',
      limit: 10
    });
    
    return filtered;
  }, [meetings]);

  // Helper function to get neighboring cities
  const getNeighboringCities = (city) => {
    const cityMap = {
      'davie': ['fort lauderdale', 'hollywood', 'weston', 'plantation', 'sunrise', 'pembroke pines'],
      'fort lauderdale': ['davie', 'hollywood', 'pompano beach', 'deerfield beach', 'coral springs'],
      'miami': ['coral gables', 'coconut grove', 'key biscayne', 'aventura', 'sunny isles'],
      'austin': ['round rock', 'cedar park', 'georgetown', 'pflugerville', 'lakeway'],
      'dallas': ['plano', 'frisco', 'mckinney', 'allen', 'richardson'],
      'houston': ['katy', 'sugar land', 'the woodlands', 'pearland', 'cypress'],
      'atlanta': [
        'chamblee',
        'decatur',
        'sandy springs',
        'alpharetta',
        'marietta',
        'duluth',
        'roswell',
      ],
      'denver': ['boulder', 'aurora', 'lakewood', 'westminster', 'arvada', 'littleton'],
      'phoenix': ['scottsdale', 'tempe', 'mesa', 'chandler', 'glendale'],
      'seattle': ['bellevue', 'redmond', 'kirkland', 'renton', 'federal way'],
      'portland': ['beaverton', 'hillsboro', 'gresham', 'tigard', 'lake oswego'],
      'san diego': ['la jolla', 'del mar', 'encinitas', 'cardiff', 'carlsbad', 'oceanside', 'solana beach'],
      'los angeles': ['beverly hills', 'santa monica', 'pasadena', 'glendale', 'burbank'],
      'san francisco': ['oakland', 'berkeley', 'san jose', 'palo alto', 'mountain view'],
      'chicago': ['evanston', 'oak park', 'naperville', 'aurora', 'joliet'],
      'boston': ['cambridge', 'somerville', 'brookline', 'newton', 'quincy'],
      'new york': ['brooklyn', 'queens', 'bronx', 'staten island', 'jersey city'],
      'washington': ['arlington', 'alexandria', 'bethesda', 'silver spring', 'rockville']
    };
    
    return cityMap[city.toLowerCase()] || [];
  };

  // Filter featured clubs based on user's city, with fallback to neighboring cities
  // Featured clubs removed - only showing user's own club
  // Update featured clubs state to empty array
  useEffect(() => {
    setFeaturedClubs([]);
    setIsShowingNeighboringCities(false);
  }, []);


  // Handle event creation
  const handleEventCreated = (eventData) => {
    // Refresh data to show the new event
    refetchMeetings();
    setShowEventForm(false);
  };

  // Handle edit meeting
  const handleEditRun = (meeting) => {
    setEditingRun(meeting);
    setShowEditForm(true);
  };

  // Handle edit form close
  const handleEditFormClose = () => {
    setShowEditForm(false);
    setEditingRun(null);
    // Refresh data in case any changes were made
    refetchMeetings();
  };

  // Handle meeting update
  const handleRunUpdated = () => {
    // Refresh data to show updated meeting
    refetchMeetings();
    setShowEditForm(false);
    setEditingRun(null);
  };

  // Handle connections click
  const handleConnectionsClick = async () => {
    if (!user?.club_id) {
      toast.error('Club information not available');
      return;
    }
    
    setShowConnections(true);
    setLoadingConnections(true);
    
    try {
      const response = await apiService.clubs.getClubMembers(user.club_id, 200, 0);
      // Extract members array from response using shared utility
      const members = parseMemberResponse(response);
      setConnections(members);
    } catch (error) {
      console.error('Failed to fetch members:', error);
      toast.error('Failed to load members');
      setConnections([]);
    } finally {
      setLoadingConnections(false);
    }
  };

  // Handle connection click
  const handleConnectionClick = async (connection) => {
    if (connection.user_id) {
      setLoadingConnectionCard(true);
      setSelectedConnection(connection);
      setShowConnectionCard(true);
      
      try {
        // Fetch the user's full profile data
        const userProfile = await apiService.users.getProfile(connection.user_id);
        setSelectedConnection(userProfile);
      } catch (error) {
        console.error('Failed to fetch user profile:', error);
        toast.error('Failed to load connection profile');
        setShowConnectionCard(false);
      } finally {
        setLoadingConnectionCard(false);
      }
    } else {
      console.error('No user_id found in connection data');
      toast.error('Unable to view connection profile');
    }
  };

  // Handle back to connections
  const handleBackToConnections = () => {
    setShowConnectionCard(false);
    setSelectedConnection(null);
  };


  if (meetingsLoading || clubsLoading || clubLoading) {
    return (
      <div className="min-h-screen bg-runcards-background">
        <Navigation />
        <div className="pt-20 flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="w-8 h-8 border-2 border-runcards-border/30 border-t-white rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-runcards-text-secondary">Loading your dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  // Handle case where club doesn't exist (404 error)
  if (user?.club_id && clubError && clubError.response?.status === 404) {
    return (
      <div className="min-h-screen bg-runcards-background">
        <Navigation />
        <div className="pt-20 flex items-center justify-center min-h-screen">
          <div className="text-center max-w-md mx-auto px-4">
            <AlertCircle className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
            <h2 className="text-runcards-xl font-runcards-bold text-runcards-text-primary mb-2">
              Club Not Found
            </h2>
            <p className="text-runcards-text-secondary mb-6">
              Your account references a club that no longer exists. Please contact support or complete your club setup again.
            </p>
            <Button
              onClick={() => navigate('/profile')}
              className="bg-runcards-accent text-white px-6 py-2 rounded-full"
            >
              Go to Profile
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // If no club_id but user has completed setup, show loading while creating club
  if (!user?.club_id && user?.is_setup_complete) {
    return (
      <div className="min-h-screen bg-runcards-background">
        <Navigation />
        <div className="pt-20 flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="w-8 h-8 border-2 border-runcards-border/30 border-t-white rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-runcards-text-secondary">Setting up your club dashboard...</p>
          </div>
        </div>
      </div>
    );
  }


  // If no club_id and not setup complete, show basic dashboard with user's club info
  if (!user?.club_id) {
    return (
      <div className="min-h-screen">
        <Navigation />
        
        {/* Modern Header */}
        <div className="px-4 py-6 pt-20">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-6">
            <div>
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 mb-2 sm:mb-4">
                <h1 className="text-runcards-xxl font-runcards-bold text-runcards-text-primary">
                  {clubData?.name || 'Your Run Club'} Dashboard
                </h1>
                {user?.city && (
                  <div className="flex items-center bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-3 py-1.5">
                    <MapPin className="w-4 h-4 text-emerald-400 mr-2" />
                    <span className="text-emerald-300 text-runcards-sm font-runcards">
                      {user.city}{user?.state ? `, ${user?.state}` : ''} • Run Club
                    </span>
                  </div>
                )}
              </div>
              {!user?.city && (
                <p className="text-runcards-text-secondary text-runcards-sm">
                  Set your location to see local clubs
                </p>
              )}
            </div>
              <div className="flex items-center space-x-4">
                <div className="flex items-center text-emerald-400">
                  <Star className="w-4 h-4 mr-1 fill-current" />
                  <span className="text-runcards-sm font-runcards">0.0</span>
                </div>
                <Badge variant="secondary" className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">
                  Active
                </Badge>
              </div>
            </div>
          </div>
        </div>

        {/* Basic Content */}
        <div className="px-4 py-8">
          <div className="max-w-7xl mx-auto">
            {/* Setup Notice */}
            <Card className="gradient-card border-yellow-500/20 mb-8">
              <CardContent className="p-6">
                <div className="flex items-center mb-4">
                  <AlertCircle className="w-6 h-6 text-yellow-400 mr-3" />
                  <h3 className="text-runcards-xl font-runcards-bold text-runcards-text-primary">Complete Your Club Setup</h3>
                </div>
                <p className="text-runcards-text-secondary mb-4">
                  To access full dashboard features and start managing your run club, you need to complete the full setup process.
                </p>
                <Button 
                  onClick={() => navigate('/profile')}
                  className="btn-primary"
                >
                  Complete Setup
                </Button>
              </CardContent>
            </Card>

            {/* Basic Club Information */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <Card className="gradient-card border-runcards-border/10">
                <CardHeader>
                  <CardTitle className="text-runcards-text-primary flex items-center">
                    <Activity className="w-5 h-5 mr-2 text-emerald-400" />
                    Club Details
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <p className="text-runcards-text-secondary text-runcards-sm">Description</p>
                    <p className="text-runcards-text-primary">Welcome to {clubData?.name || 'Your Run Club'}! Join us for regular runs and community events.</p>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-runcards-text-secondary text-runcards-sm">Type</p>
                      <Badge variant="secondary" className="mt-1">
                        Run Club
                      </Badge>
                    </div>
                    <div>
                      <p className="text-runcards-text-secondary text-runcards-sm">Location</p>
                      <p className="text-runcards-text-primary">{clubData?.city}{clubData?.state ? `, ${clubData?.state}` : ''}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="gradient-card border-runcards-border/10">
                <CardHeader>
                  <CardTitle className="text-runcards-text-primary flex items-center">
                    <BarChart3 className="w-5 h-5 mr-2 text-emerald-400" />
                    Next Steps
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-3">
                    <div className="flex items-center text-runcards-text-secondary">
                      <CheckCircle className="w-5 h-5 text-green-400 mr-2" />
                      <span>Basic club information updated</span>
                    </div>
                    
                    <div className="flex items-center text-yellow-400">
                      <Clock className="w-5 h-5 mr-2" />
                      <span>Complete full setup to access dashboard</span>
                    </div>
                    
                    <div className="flex items-center text-runcards-text-secondary">
                      <Users className="w-5 h-5 mr-2" />
                      <span>Set up meeting schedules</span>
                    </div>
                  </div>
                  
                  <div className="pt-4 border-t border-runcards-border/10">
                    <Button 
                      onClick={() => navigate('/profile')}
                      className="w-full btn-primary"
                    >
                      Complete Full Setup
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    );
  }


  const getDayColor = (day) => {
    const colors = {
      'Monday': 'bg-blue-500/20 text-blue-300 border-blue-500/30',
      'Tuesday': 'bg-green-500/20 text-green-300 border-green-500/30',
      'Wednesday': 'bg-purple-500/20 text-purple-300 border-purple-500/30',
      'Thursday': 'bg-orange-500/20 text-orange-300 border-orange-500/30',
      'Friday': 'bg-pink-500/20 text-pink-300 border-pink-500/30',
      'Saturday': 'bg-indigo-500/20 text-indigo-300 border-indigo-500/30',
      'Sunday': 'bg-red-500/20 text-red-300 border-red-500/30'
    };
    return colors[day] || 'bg-gray-500/20 text-runcards-text-secondary border-gray-500/30';
  };

  return (
    <div className="min-h-screen bg-runcards-background">
      <Navigation />
      
      {/* Modern Header */}
      <div className="px-4 py-6 pt-20">
        <div className="max-w-7xl mx-auto">
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-4">
                <h1 className="text-runcards-xxl font-runcards-bold text-runcards-text-primary font-runcards-bold">
                  {clubData?.name || 'Run Club'} Dashboard
                </h1>
                <Button
                  onClick={() => {
                    refetchClub();
                    refetchMeetings();
                  }}
                  variant="outline"
                  size="sm"
                  className="bg-white/10 border-runcards-border/20 text-runcards-text-primary hover:bg-white/20"
                >
                  <Activity className="w-4 h-4 mr-2" />
                  Refresh
                </Button>
              </div>
              <Badge variant="secondary" className="bg-white/20 text-runcards-text-primary border-runcards-border/30">
                Active
              </Badge>
            </div>
            
            {/* Location and Rating - moved to left side under hero */}
            <div className="flex items-center gap-4">
              <div className="flex items-center bg-white/10 border border-runcards-border/20 rounded-lg px-3 py-1.5">
                <MapPin className="w-4 h-4 text-runcards-text-primary mr-2" />
                <span className="text-runcards-text-primary text-runcards-sm font-runcards font-runcards">
                  {user?.city || 'Unknown Location'} • Run Club
                </span>
              </div>
              <div className="flex items-center text-runcards-text-primary">
                <Star className="w-4 h-4 mr-1 fill-current" />
                <span className="text-runcards-sm font-runcards font-runcards">0.0</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-4 py-8">
        <div className="max-w-7xl mx-auto">
          {/* Stats Overview */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <Card 
              className="bg-runcards-background/50 backdrop-blur-xl border border-runcards-border/20 rounded-2xl cursor-pointer hover:bg-runcards-background/60 transition-colors"
              onClick={handleConnectionsClick}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-runcards-text-secondary text-runcards-xs font-runcards uppercase tracking-wide font-runcards">Total Members</p>
                    <p className="text-runcards-xxl font-runcards-bold text-runcards-text-primary mt-1 font-runcards-bold">
                      {clubLoading ? '...' : (clubData?.members || 0)}
                    </p>
                    {/* Debug info - remove in production */}
                    {process.env.NODE_ENV === 'development' && (
                      <p className="text-runcards-xs text-runcards-text-secondary mt-1">
                        Debug: {JSON.stringify({ clubData: clubData?.members, loading: clubLoading })}
                      </p>
                    )}
                    <p className="text-runcards-text-primary/80 text-runcards-xs flex items-center mt-2 font-runcards">
                      <TrendingUp className="w-3 h-3 mr-1" />
                      {clubLoading ? 'Loading...' : 'Click to view members'}
                    </p>
                  </div>
                  <div className="p-2 bg-white/10 rounded-lg">
                    <Users className="w-5 h-5 text-runcards-text-primary" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-runcards-background/50 backdrop-blur-xl border border-runcards-border/20 rounded-2xl">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-runcards-text-secondary text-runcards-xs font-runcards uppercase tracking-wide font-runcards">Confirmed Runs</p>
                    <p className="text-runcards-xxl font-runcards-bold text-runcards-text-primary mt-1 font-runcards-bold">
                      {meetingsLoading ? '...' : upcomingEvents.length}
                    </p>
                    <p className="text-runcards-text-primary/80 text-runcards-xs flex items-center mt-2 font-runcards">
                      <Activity className="w-3 h-3 mr-1" />
                      Upcoming runs
                    </p>
                  </div>
                  <div className="p-2 bg-white/10 rounded-lg">
                    <CheckCircle className="w-5 h-5 text-runcards-text-primary" />
                  </div>
                </div>
              </CardContent>
            </Card>


            <Card className="bg-runcards-background/50 backdrop-blur-xl border border-runcards-border/20 rounded-2xl">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-runcards-text-secondary text-runcards-xs font-runcards uppercase tracking-wide font-runcards">This Week</p>
                    <p className="text-runcards-xxl font-runcards-bold text-runcards-text-primary mt-1 font-runcards-bold">0</p>
                    <p className="text-runcards-text-primary/80 text-runcards-xs flex items-center mt-2 font-runcards">
                      <Calendar className="w-3 h-3 mr-1" />
                      New registrations
                    </p>
                  </div>
                  <div className="p-2 bg-white/10 rounded-lg">
                    <BarChart3 className="w-5 h-5 text-runcards-text-primary" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Upcoming Runs This Week */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-runcards-xl font-runcards-bold text-runcards-text-primary font-runcards-bold">This Week's Runs</h2>
              <Button 
                onClick={() => setShowEventForm(true)}
                className="bg-white text-black px-4 py-2 text-runcards-sm font-runcards rounded-lg hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-black font-runcards"
              >
                <Plus className="w-4 h-4 mr-2" />
                Schedule a Run
              </Button>
            </div>
            {upcomingEvents.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                {upcomingEvents.map((event, index) => (
                  <MeetingCard
                    key={`upcoming-event-${event.id || index}`}
                    event={event}
                    onButtonClick={() => handleEditRun(event)}
                    buttonText="Edit Run"
                    showHover={false}
                  />
                ))}
              </div>
            ) : (
              <Card className="bg-runcards-background/50 backdrop-blur-xl border border-runcards-border/20 rounded-2xl">
                <CardContent className="p-8 text-center">
                  <div className="p-4 bg-white/10 rounded-full w-20 h-20 mx-auto mb-4 flex items-center justify-center">
                    <Calendar className="w-8 h-8 text-runcards-text-primary" />
                  </div>
                  <h3 className="text-runcards-lg font-runcards-bold text-runcards-text-primary mb-2 font-runcards-bold">No runs scheduled this week</h3>
                  <p className="text-runcards-text-primary/80 mb-6 text-runcards-sm font-runcards">
                    Your club doesn't have any runs scheduled for this week.
                  </p>
                  <Button 
                    onClick={() => setShowEventForm(true)}
                    className="bg-white text-black px-6 py-3 text-runcards-md font-runcards rounded-lg hover:bg-gray-100 font-runcards"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Schedule Your First Run
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Club Information */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="bg-runcards-background/50 backdrop-blur-xl border border-runcards-border/20 rounded-2xl">
              <CardHeader>
                <CardTitle className="text-runcards-text-primary flex items-center font-runcards-bold">
                  <Activity className="w-5 h-5 mr-2 text-runcards-text-primary" />
                  Club Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-runcards-text-secondary text-runcards-sm font-runcards">Description</p>
                  <p className="text-runcards-text-primary font-runcards">Run club for {user?.city || 'your area'}</p>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-runcards-text-secondary text-runcards-sm font-runcards">Type</p>
                    <Badge className="mt-1 bg-white/20 text-runcards-text-primary border-runcards-border/30">
                      Run Club
                    </Badge>
                  </div>
                  <div>
                    <p className="text-runcards-text-secondary text-runcards-sm font-runcards">Location</p>
                    <p className="text-runcards-text-primary font-runcards">{user?.city || 'Unknown Location'}</p>
                  </div>
                </div>
                
                <div>
                  <p className="text-runcards-text-secondary text-runcards-sm font-runcards">Capacity</p>
                  <p className="text-runcards-text-primary font-runcards">Unlimited connections</p>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-runcards-background/50 backdrop-blur-xl border border-runcards-border/20 rounded-2xl">
              <CardHeader>
                <CardTitle className="text-runcards-text-primary flex items-center font-runcards-bold">
                  <BarChart3 className="w-5 h-5 mr-2 text-runcards-text-primary" />
                  Registration Summary
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-runcards-text-primary/80 font-runcards">Total Registrations</span>
                    <span className="text-runcards-text-primary font-runcards-bold font-runcards-bold">0</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-runcards-text-primary/80 font-runcards">Confirmed</span>
                    <span className="text-runcards-text-primary font-runcards-bold font-runcards-bold">0</span>
                  </div>
                </div>
                
                <div className="pt-4 border-t border-runcards-border/20">
                  <div className="flex items-center justify-between text-runcards-sm">
                    <span className="text-runcards-text-primary/60 font-runcards">Made with</span>
                    <span className="text-runcards-text-primary font-runcards font-runcards">Runcards</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Featured Clubs Section */}
      <section className="py-12 sm:py-16 lg:py-20 px-4 sm:px-6 bg-runcards-background">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-8 sm:mb-12 gap-4 sm:gap-0">
            <div className="text-center sm:text-left">
              <h2 className="text-runcards-xxl sm:text-runcards-title lg:text-4xl font-runcards-bold text-runcards-text-primary mb-4 font-runcards-bold">Who's Running {user?.city ? user.city.split(',')[0] : 'Your City'}</h2>
            </div>
            <Button 
              variant="outline" 
              className="border-runcards-border/20 text-runcards-text-primary hover:bg-white/10 hover:text-runcards-text-primary w-full sm:w-auto"
              onClick={() => navigate('/runclubs')}
            >
              <span className="hidden sm:inline">View All Clubs</span>
              <span className="sm:hidden">View All</span>
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
          
          {featuredClubs.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
              {featuredClubs.map((club, index) => (
                <ClubCard
                  key={`featured-club-${club.id || index}`} 
                  club={club}
                  buttonText="Join Club"
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="bg-runcards-background/50 backdrop-blur-xl border border-runcards-border/20 rounded-2xl p-8 max-w-md mx-auto">
                <MapPin className="w-16 h-16 text-runcards-text-primary mx-auto mb-4" />
                <h3 className="text-runcards-xl font-runcards-bold text-runcards-text-primary mb-2 font-runcards-bold">
                  No clubs found
                </h3>
                <p className="text-runcards-text-primary/80 mb-6 font-runcards">
                  We don't have any running clubs in your area yet. Try checking out clubs in other locations.
                </p>
                <Button 
                  className="bg-white text-black px-6 py-3 text-runcards-md font-runcards rounded-lg hover:bg-gray-100 font-runcards"
                  onClick={() => navigate('/runclubs')}
                >
                  Browse All Clubs
                </Button>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Event Creation Form Modal */}
      {showEventForm && (
        <EventCreationForm
          onClose={() => setShowEventForm(false)}
          onEventCreated={handleEventCreated}
        />
      )}

      {/* Edit Meeting Form Modal */}
      {showEditForm && editingRun && (
        <EventCreationForm
          onClose={handleEditFormClose}
          onEventCreated={handleRunUpdated}
          editingRun={editingRun}
          isEditing={true}
        />
      )}

      {/* Connections Modal */}
      <Dialog open={showConnections} onOpenChange={setShowConnections}>
        <DialogContent className="bg-runcards-background/90 backdrop-blur-xl border border-runcards-border/20 max-w-md mx-auto shadow-2xl">
          {!showConnectionCard ? (
            // Connections List View
            <>
              <DialogHeader className="pb-4">
                <DialogTitle className="text-runcards-text-primary flex items-center text-runcards-xl font-runcards-bold font-runcards-bold">
                  <Users className="w-6 h-6 mr-3" />
                  Connections ({connections.length})
                </DialogTitle>
                <DialogDescription className="text-runcards-text-primary/70 text-runcards-sm mt-1 font-runcards">
                  Connections of your run club
                </DialogDescription>
              </DialogHeader>
              
              <div className="max-h-80 overflow-y-auto">
                {loadingConnections ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="text-runcards-text-primary/60 font-runcards">
                      Loading connections...
                    </div>
                  </div>
                ) : connections.length === 0 ? (
                  <div className="text-center py-8">
                    <div className="text-runcards-text-primary/60 font-runcards">
                      No connections yet
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {connections.map((connection, index) => {
                      return (
                      <div key={connection.user_id || index} className="flex items-center space-x-4 p-4 bg-white/5 rounded-xl border border-runcards-border/10 hover:bg-white/10 transition-colors">
                        <div className="w-12 h-12 bg-white/10 border border-runcards-border/20 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden">
                          {connection.profile_picture ? (
                            <img 
                              src={connection.profile_picture} 
                              alt={connection.name}
                              className="w-full h-full rounded-full object-cover"
                            />
                          ) : (
                            <Users className="w-6 h-6 text-runcards-text-primary/60" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <button
                            onClick={() => handleConnectionClick(connection)}
                            className="text-runcards-text-primary font-runcards-bold hover:text-runcards-text-primary transition-colors duration-200 text-left w-full text-runcards-md border-0 focus:outline-none font-runcards"
                          >
                            {connection.name || 'Unknown Connection'}
                          </button>
                        </div>
                      </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          ) : (
            // Connection Card View
            <>
              {/* Hidden accessibility elements */}
              <DialogHeader className="sr-only">
                <DialogTitle>Connection Card</DialogTitle>
                <DialogDescription>
                  {selectedConnection?.name ? `Connection card for ${selectedConnection.name}` : 'Loading connection card'}
                </DialogDescription>
              </DialogHeader>
              
              <div className="relative">
                <div className="flex justify-center">
                  {loadingConnectionCard ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="text-runcards-text-primary/60 font-runcards">
                        Loading connection card...
                      </div>
                    </div>
                  ) : selectedConnection ? (
                    <RunCard 
                      runner={selectedConnection}
                      showHover={false}
                      buttonText="View Profile"
                      onButtonClick={() => {
                        toast.info('Profile details coming soon');
                      }}
                    />
                  ) : (
                    <div className="text-center py-8">
                      <div className="text-runcards-text-primary/60 font-runcards">
                        Unable to load connection card
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RunClubDashboard;